import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { track } from "../firebase";
import { CONFIG } from "./config";
import { buildPayload, sendLead } from "./lead";
import { buildSteps } from "./steps";
import {
  emptyAnswers,
  hasSingleKey,
  type Answers,
  type Step,
  type Variant,
} from "./types";
import "./driver-form.css";

/** 選択したら自動で次へ進むまでの待ち時間（チェックの視認に必要な間） */
const AUTO_ADVANCE_MS = 260;

export default function DriverForm({ variant }: { variant: Variant }) {
  const steps = useMemo(() => buildSteps(variant), [variant]);
  const [a, setA] = useState<Answers>(emptyAnswers);
  const [cur, setCur] = useState(0);
  const [err, setErr] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const cardRef = useRef<HTMLFormElement>(null);
  const advanceRef = useRef<number | undefined>(undefined);
  /** 自動送りのタイマーは発火時点の最新の回答を見る必要があるため ref で保持します */
  const latest = useRef({ a, cur });
  latest.current = { a, cur };

  const step = steps[cur];

  /** when を満たすステップだけが進捗と前後移動の対象になります */
  const visible = useMemo(() => steps.filter((s) => !s.when || s.when(a)), [steps, a]);
  const isLast = visible[visible.length - 1] === step;

  /**
   * 進捗の表示はステップが変わったときだけ更新します。
   * 郵便番号を入れると希望エリアの設問が消えるため、回答に追従させると
   * 入力の途中で分母が動いてしまいます。
   */
  const [meter, setMeter] = useState({ pos: 0, total: 0 });
  useEffect(() => {
    const list = steps.filter((s) => !s.when || s.when(latest.current.a));
    setMeter({ pos: Math.max(0, list.indexOf(steps[cur])), total: list.length });
  }, [cur, steps]);
  const { pos, total } = meter;
  const progress = total > 1 ? (pos / (total - 1)) * 100 : 0;

  /** 移動先は常に最新の回答で絞った一覧から決めます */
  const go = useCallback(
    (dir: 1 | -1) => {
      const target = visible[visible.indexOf(step) + dir];
      if (!target) return;
      setErr("");
      setCur(steps.indexOf(target));
    },
    [visible, step, steps]
  );

  /* ステップが変わったら先頭へ。PCではテキスト入力にフォーカスを当てます */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (!matchMedia("(min-width:600px)").matches) return;
    const input = cardRef.current?.querySelector<HTMLInputElement>(
      "input[type=text],input[type=tel]"
    );
    input?.focus();
  }, [cur]);

  /* 保留中の自動送りはアンマウント時に必ず止めます */
  useEffect(() => () => window.clearTimeout(advanceRef.current), []);

  /**
   * 単一選択で呼ばれ、少し待ってから次のステップへ送ります。
   * 送り先はタイマー発火時点の回答から計算します。免許の選択で AT の設問が
   * 出現するように、選択そのものが次のステップを変えるためです。
   */
  const autoAdvance = useCallback(() => {
    window.clearTimeout(advanceRef.current);
    const from = cur;
    advanceRef.current = window.setTimeout(() => {
      const { a: now, cur: nowCur } = latest.current;
      if (nowCur !== from) return; // 手動で先に進んでいたら何もしない
      const list = steps.filter((s) => !s.when || s.when(now));
      const target = list[list.indexOf(steps[from]) + 1];
      if (!target) return;
      setErr("");
      setCur(steps.indexOf(target));
    }, AUTO_ADVANCE_MS);
  }, [cur, steps]);

  /* ---------- 検証 ---------- */
  function validate(s: Step, ans: Answers): string {
    if (s.type === "name" && s.required && (!ans.last || !ans.first)) {
      return "姓と名を入力してください。";
    }
    if (s.type === "birth") {
      if (s.required && !ans.birth) return "年・月・日をすべて選択してください。";
      return s.validate?.(ans.birth) ?? "";
    }
    if (s.type === "consent" && !ans.agree) return "利用規約への同意が必要です。";
    if (hasSingleKey(s)) {
      const v = ans[s.key];
      if (s.required && !v) {
        return s.type === "radio" || s.type === "tiles"
          ? "ひとつ選んでください。"
          : "入力してください。";
      }
      if ("validate" in s && s.validate) return s.validate(v);
    }
    return "";
  }

  /* ---------- 送信 ---------- */
  async function submit() {
    if (sending) return;
    setSending(true);
    try {
      await sendLead(await buildPayload(a, variant));
      void track("generate_lead", { variant });
      if (CONFIG.thanksUrl) {
        location.href = CONFIG.thanksUrl;
        return;
      }
      setDone(true);
    } catch {
      setErr("送信できませんでした。通信環境を確認してもう一度お試しください。");
    } finally {
      setSending(false);
    }
  }

  function next() {
    window.clearTimeout(advanceRef.current);
    const msg = validate(step, a);
    setErr(msg);
    if (msg) return;
    if (isLast) void submit();
    else go(1);
  }

  /* ---------- 郵便番号 → 住所（任意機能／失敗しても進行に影響しません） ---------- */
  async function lookupZip(raw: string) {
    const z = raw.replace(/[^\d]/g, "");
    if (z.length !== 7) {
      setA((p) => ({ ...p, zipAddr: "" }));
      return;
    }
    try {
      const r = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${z}`);
      const j = (await r.json()) as {
        results?: { address1: string; address2: string; address3: string }[];
      };
      const hit = j.results?.[0];
      setA((p) => ({ ...p, zipAddr: hit ? `${hit.address1}${hit.address2}${hit.address3}` : "" }));
    } catch {
      setA((p) => ({ ...p, zipAddr: "" }));
    }
  }

  /** 「なし／特になし」と他の選択肢は互いに排他にします */
  function toggleMulti(key: "quals" | "prefer", value: string, none: string, checked: boolean) {
    setA((p) => {
      const cur = p[key];
      if (value === none) return { ...p, [key]: checked ? [none] : [] };
      const kept = cur.filter((v) => v !== value && v !== none);
      return { ...p, [key]: checked ? [...kept, value] : kept };
    });
  }

  if (done) return <Thanks />;

  return (
    <>
      <header className="head">
        <div className="head__row">
          <div className="brand">
            Workful <span>DRIVER</span>
          </div>
          <div className="count">
            <b>{pos + 1}</b> / <span>{total}</span>
          </div>
        </div>
        <div className="road">
          <div className="road__line">
            <div className="road__fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="truck" style={{ left: `${progress}%` }}>
            <TruckIcon />
          </div>
        </div>
      </header>

      <main>
        <form
          className="card"
          ref={cardRef}
          noValidate
          onSubmit={(e) => e.preventDefault()}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            next();
          }}
        >
          {/* key を付けることでステップ切り替え時のスライドインが再生されます */}
          <section className="step is-on" key={step.id}>
            <Head step={step} />
            <Body
              step={step}
              a={a}
              setA={setA}
              onPick={autoAdvance}
              onToggleMulti={toggleMulti}
              onZipInput={lookupZip}
            />
            <div className="err" hidden={!err}>
              {err}
            </div>
          </section>
        </form>
      </main>

      <footer className="foot">
        <div className="foot__in">
          <button
            type="button"
            className="back"
            hidden={pos === 0}
            aria-label="前の質問へ戻る"
            onClick={() => go(-1)}
          >
            ‹
          </button>
          <button
            type="button"
            className={isLast ? "btn send" : "btn"}
            disabled={sending}
            onClick={next}
          >
            {sending ? "送信中…" : isLast ? "登録を送信する" : "次へ"}
          </button>
        </div>
      </footer>
    </>
  );
}

/* ============================================================
   見出し
   ============================================================ */
function Head({ step }: { step: Step }) {
  if (step.type === "consent") {
    return (
      <h2>
        {step.title}
        <span className="req">必須</span>
      </h2>
    );
  }
  return (
    <>
      <h2>
        {step.title}
        {step.required ? (
          <span className="req">必須</span>
        ) : (
          <span className="opt">任意</span>
        )}
      </h2>
      {step.help && <p className="help">{step.help}</p>}
    </>
  );
}

/* ============================================================
   設問本体
   ============================================================ */
interface BodyProps {
  step: Step;
  a: Answers;
  setA: React.Dispatch<React.SetStateAction<Answers>>;
  onPick: () => void;
  onToggleMulti: (key: "quals" | "prefer", value: string, none: string, checked: boolean) => void;
  onZipInput: (v: string) => void;
}

function Body({ step: s, a, setA, onPick, onToggleMulti, onZipInput }: BodyProps) {
  switch (s.type) {
    case "radio":
      return (
        <div className="fields">
          {s.options.map((o) => (
            <label className="opt-row" key={o}>
              <input
                type="radio"
                name={s.key}
                value={o}
                checked={a[s.key] === o}
                onChange={() => {
                  setA((p) => ({ ...p, [s.key]: o }));
                  onPick();
                }}
              />
              <span className="mark" />
              <span className="opt-txt">{o}</span>
            </label>
          ))}
        </div>
      );

    case "tiles":
      return (
        <div className="fields tiles">
          {s.options.map((o) => (
            <label className="opt-row" key={o.v}>
              <input
                type="radio"
                name={s.key}
                value={o.v}
                checked={a[s.key] === o.v}
                onChange={() => {
                  setA((p) => ({ ...p, [s.key]: o.v }));
                  onPick();
                }}
              />
              <span className="mark" />
              <img className="tile-img" src={o.img} alt="" loading="lazy" />
              <span className="opt-txt">{o.v}</span>
            </label>
          ))}
        </div>
      );

    case "check":
      return (
        <div className="fields">
          {[...s.options, s.none].map((o) => (
            <label className="opt-row" key={o}>
              <input
                type="checkbox"
                name={s.key}
                value={o}
                checked={a[s.key].includes(o)}
                onChange={(e) => onToggleMulti(s.key, o, s.none, e.target.checked)}
              />
              <span className="mark sq" />
              <span className="opt-txt">{o}</span>
            </label>
          ))}
        </div>
      );

    case "checkImg":
      return (
        <div className="fields grid2">
          {s.options.map((o) => (
            <label className="opt-row card-opt" key={o.v}>
              <input
                type="checkbox"
                name={s.key}
                value={o.v}
                checked={a[s.key].includes(o.v)}
                onChange={(e) => onToggleMulti(s.key, o.v, s.none, e.target.checked)}
              />
              <span className="mark sq" />
              <img className="tile-img" src={o.img} alt="" loading="lazy" />
              <span className="opt-txt">{o.v}</span>
            </label>
          ))}
          <label className="opt-row card-opt none-opt">
            <input
              type="checkbox"
              name={s.key}
              value={s.none}
              checked={a[s.key].includes(s.none)}
              onChange={(e) => onToggleMulti(s.key, s.none, s.none, e.target.checked)}
            />
            <span className="mark sq" />
            <span className="opt-txt">{s.none}</span>
          </label>
        </div>
      );

    case "text":
    case "tel":
      return (
        <>
          <div className="fields">
            <input
              type={s.type === "tel" ? "tel" : "text"}
              name={s.key}
              inputMode={s.mode ?? "text"}
              placeholder={s.placeholder ?? ""}
              maxLength={s.maxlength}
              autoComplete={s.key === "tel" ? "tel" : s.key === "zip" ? "postal-code" : "off"}
              value={a[s.key]}
              onChange={(e) => {
                const v = e.target.value;
                setA((p) => ({ ...p, [s.key]: v }));
                if (s.zip) onZipInput(v);
              }}
            />
          </div>
          {s.zip && <p className="zipnote">{a.zipAddr}</p>}
        </>
      );

    case "birth":
      return <BirthFields a={a} setA={setA} />;

    case "name":
      return (
        <div className="fields">
          <input
            type="text"
            name="last"
            placeholder="姓"
            autoComplete="family-name"
            value={a.last}
            onChange={(e) => setA((p) => ({ ...p, last: e.target.value.trimStart() }))}
          />
          <input
            type="text"
            name="first"
            placeholder="名"
            autoComplete="given-name"
            value={a.first}
            onChange={(e) => setA((p) => ({ ...p, first: e.target.value.trimStart() }))}
          />
        </div>
      );

    case "consent":
      return <Consent a={a} setA={setA} />;
  }
}

/* ---------- 生年月日 ---------- */
function BirthFields({
  a,
  setA,
}: {
  a: Answers;
  setA: React.Dispatch<React.SetStateAction<Answers>>;
}) {
  // 16歳未満は就業対象外なので、選べる最新の年を「今年 - 16」に切ります
  const years = useMemo(() => {
    const now = new Date().getFullYear();
    const out: number[] = [];
    for (let y = now - 16; y >= 1940; y--) out.push(y);
    return out;
  }, []);
  const nums = (n: number) => Array.from({ length: n }, (_, i) => i + 1);

  const update = (part: "by" | "bm" | "bd", value: string) =>
    setA((p) => {
      const nextA = { ...p, [part]: value };
      nextA.birth =
        nextA.by && nextA.bm && nextA.bd
          ? `${nextA.by}-${nextA.bm.padStart(2, "0")}-${nextA.bd.padStart(2, "0")}`
          : "";
      return nextA;
    });

  const col = (
    name: "by" | "bm" | "bd",
    label: string,
    list: number[],
    pad: boolean
  ) => (
    <div className="bcol">
      <select
        name={name}
        aria-label={label}
        value={a[name]}
        onChange={(e) => update(name, e.target.value)}
      >
        <option value="">--</option>
        {list.map((v) => (
          <option value={v} key={v}>
            {pad ? String(v).padStart(2, "0") : v}
          </option>
        ))}
      </select>
      <span>{label}</span>
    </div>
  );

  return (
    <div className="fields birth">
      {col("by", "年", years, false)}
      {col("bm", "月", nums(12), true)}
      {col("bd", "日", nums(31), true)}
    </div>
  );
}

/* ---------- 同意 ---------- */
function Consent({
  a,
  setA,
}: {
  a: Answers;
  setA: React.Dispatch<React.SetStateAction<Answers>>;
}) {
  return (
    <>
      <div className="scope">
        <p>
          <b>ご登録内容の提供先</b>
          <br />
          あなたの登録内容は、ご希望のエリア・免許に対応する
          <b>人材紹介会社（最大{CONFIG.maxPartners}社）</b>
          に提供され、各社から求人のご案内のお電話・SMSが届きます。提供先の会社は
          <a href={CONFIG.partnersUrl} target="_blank" rel="noopener noreferrer">
            提携会社一覧
          </a>
          でご確認いただけます。
        </p>
        <p>
          <b>提供する項目</b>
          <br />
          お名前・携帯電話番号・郵便番号（または希望エリア）・免許区分・保有資格・就業状況・希望条件・生年月日・性別
        </p>
        <p>
          <b>ご利用料金</b>
          <br />
          求職者の方は無料です。
        </p>
      </div>
      <label className="consent">
        <input
          type="checkbox"
          name="agree"
          checked={a.agree}
          onChange={(e) => setA((p) => ({ ...p, agree: e.target.checked }))}
        />
        <span className="mark sq" />
        <span className="opt-txt">
          <a href={CONFIG.termsUrl} target="_blank" rel="noopener noreferrer">
            利用規約
          </a>
          ・
          <a href={CONFIG.privacyUrl} target="_blank" rel="noopener noreferrer">
            個人情報の取扱い
          </a>
          を確認し、上記の会社への提供に同意する
        </span>
      </label>
      <p className="corp">
        {CONFIG.company}（特定募集情報等提供事業者 届出受理番号 {CONFIG.noticeNo}）
        <br />
        {CONFIG.address}
      </p>
    </>
  );
}

/* ---------- 完了画面 ---------- */
function Thanks() {
  return (
    <>
      <header className="head">
        <div className="head__row">
          <div className="brand">
            Workful <span>DRIVER</span>
          </div>
        </div>
        <div className="road">
          <div className="road__line">
            <div className="road__fill" style={{ width: "100%" }} />
          </div>
          <div className="truck" style={{ left: "100%" }}>
            <TruckIcon />
          </div>
        </div>
      </header>
      <main>
        <div className="card">
          <section className="step is-on done">
            <div className="tick">✓</div>
            <h2>登録が完了しました</h2>
            <p className="help">
              ご希望に合う人材紹介会社（最大{CONFIG.maxPartners}社）から、1〜2営業日以内にお電話またはSMSでご連絡します。
              <br />
              知らない番号からの着信になりますので、ご注意ください。
            </p>
            <p className="help" style={{ marginTop: 14 }}>
              提供を停止したい場合は {CONFIG.company} までご連絡ください。
            </p>
          </section>
        </div>
      </main>
    </>
  );
}

/** 進捗バーの先頭を走るトラック。この画面の署名要素です */
function TruckIcon() {
  return (
    <svg viewBox="0 0 34 34" aria-hidden="true">
      <circle cx="17" cy="17" r="15" fill="#0F1A2B" stroke="#F2A81D" strokeWidth="2" />
      <path d="M7 20V12h11v8H7z" fill="#F4F2ED" />
      <path d="M18 20v-5h5l3 3v2h-8z" fill="#F2A81D" />
      <circle cx="11.5" cy="21" r="2.4" fill="#F4F2ED" />
      <circle cx="22" cy="21" r="2.4" fill="#F4F2ED" />
      <circle cx="11.5" cy="21" r="1" fill="#0F1A2B" />
      <circle cx="22" cy="21" r="1" fill="#0F1A2B" />
    </svg>
  );
}
